import { fetchCommissionsDecoded } from '../client/fetchDecoded'
import persistentStore from '../persistentStore'

/**
 * Creates a set of brand-related service methods.
 *
 * @param {string} baseUrl - The base URL for the API endpoints.
 * @returns {Object} An object containing methods to interact with brand-related API endpoints.
 * 
 * @property {Function} getSlugSellerBrands - Fetches brands associated with a specific seller slug.
 * @param {Object} getSlugSellerBrands.params - Parameters for the request.
 * @param {string} getSlugSellerBrands.params.slug_seller - The seller's slug.
 * @param {string} [getSlugSellerBrands.params.token] - Optional authorization token.
 * @returns {Promise<Object>} A promise that resolves to the data for the seller's brands.
 * 
 * @property {Function} getAllBrands - Fetches all available brands.
 * @returns {Promise<Object>} A promise that resolves to the data for all brands.
 */
const brandServices = baseUrl => ({
  getSlugSellerBrands: async ({ slug_seller, token }) => {
    /**
     * Fetches decoded commission data for a specific brand using the provided seller slug.
     *
     * @async
     * @function
     * @param {string} slug_seller - The unique identifier (slug) of the seller/brand.
     * @returns {Promise<Object>} A promise that resolves to the decoded commission data.
     * @throws {Error} Throws an error if the fetch operation fails.
     *
     * @requires fetchCommissionsDecoded - A function to fetch and decode commission data.
     * @requires persistentStore.getToken - A method to retrieve the stored authentication token.
     *
     * @example
     * const commissions = await fetchCommissionsDecoded('/brands/seller123', {
     *   headers: {
     *     authorization: 'Bearer token',
     *     app: 'myAppName'
     *   }
     * });
     */
    const data = await fetchCommissionsDecoded(
      `${baseUrl}/brands/${slug_seller}`,
      {
        headers: {
          authorization: token || persistentStore.getToken(),
          app: process.env.REACT_APP_APPLICATION_NAME
        }
      }
    )
    return data
  },
  getAllBrands: async () => {
    /**
     * Fetches and decodes commission data for brands from the specified API endpoint.
     *
     * @async
     * @function
     * @param {string} baseUrl - The base URL of the API.
     * @returns {Promise<Object>} The decoded commission data for brands.
     * @throws {Error} If the fetch operation fails or the response is invalid.
     *
     * @example
     * const baseUrl = "https://api.example.com";
     * const data = await fetchCommissionsDecoded(`${baseUrl}/brands`, {
     *   headers: {
     *     authorization: persistentStore.getToken(),
     *     app: process.env.REACT_APP_APPLICATION_NAME
     *   }
     * });
     */
    const data = await fetchCommissionsDecoded(`${baseUrl}/brands`, {
      headers: {
        authorization: persistentStore.getToken(),
        app: process.env.REACT_APP_APPLICATION_NAME
      }
    })
    return data
  }
})

/**
 * Initializes and exports the brandServices module using the API path
 * specified in the environment variable `REACT_APP_API_COMMISIONS_PATH`.
 *
 * @module brandServices
 * @param {string} apiPath - The base API path for the brand services, 
 * retrieved from the environment variable `REACT_APP_API_COMMISIONS_PATH`.
 * @returns {Object} The initialized brandServices module.
 */
export default brandServices(process.env.REACT_APP_API_COMMISIONS_PATH)
